import {
  HereMapInterface,
  MapParams,
  ContainerRef,
  MapPadding,
  Zoom,
  CenterCoords,
  MapType,
  OnMapEventListener,
  MapEventListener,
  OnChangeBounds,
  OnChangeMapStreetView,
} from '../types';
import { FitBounds } from 'common/types/map/fitBounds';
import config from 'config';

import { MAP_NAMES } from 'entities/application';
import { DEFAULT_MAP_CENTER, DEFAULT_MAP_ZOOM } from '../constants';

// import mapStyles from './mapStyles.json';

export class HereMap implements HereMapInterface {
  name: string;
  behavior?: any;
  private _map?: Here;
  private _containerRef: ContainerRef;
  private _platform: any;
  private _tilePlatform: any;
  private _lang: string = 'en';
  private _mapUI?: any;
  private _defaultLayers: any;
  private _mapFeatures =
    'vehicle_restrictions:active_and_inactive,pois:disabled,environmental_zones:all,congestion_zones:all';
  private _pixelRatio = window.devicePixelRatio || 1;
  private _largeBreakpoints = {
    width: 4096,
    height: 2160,
  };
  private _middleBreakpoints = {
    width: 1920,
    height: 1080,
  };
  private _tilePPI = {
    100: 'DEFAULT',
    DEFAULT: 200,
    200: 'MIDDLE',
    MIDDLE: 200,
    400: 'LARGE',
    LARGE: 400,
  };
  private _layers: Map<string, H.map.layer.TileLayer> = new Map();
  private _targetZoom: number | null = null;
  private _targetCenter: { lat: number; lng: number } | null = null;
  private _wheelTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor(containerRef: ContainerRef) {
    this._containerRef = containerRef;
    this.name = MAP_NAMES.here;
  }

  initMap(params?: MapParams) {
    const { center = DEFAULT_MAP_CENTER, lang = 'en' } = params || {};
    this._lang = lang;

    if (this._containerRef) {
      this._platform = new window.H.service.Platform({
        apikey: config.hereAppKey,
      });


      var defaultLayers = this._platform.createDefaultLayers({
        lg: lang,
      });

      this._defaultLayers = defaultLayers;

      defaultLayers.vector.normal.truck.setMin(3);

      defaultLayers.raster.normal.map = this._getBaseLayer();

      this._map = new window.H.Map(
        this._containerRef,
        defaultLayers.raster.normal.map,
        {
          zoom: DEFAULT_MAP_ZOOM,
          center,
        }
      );

      window.map = this._map;      

      const vehicleRestrictionsLayer = this._getVehicleRestrictionsLayer();
      this._map.addLayer(vehicleRestrictionsLayer);

      this._map.getBaseLayer().setMin(2);

      this._resizeMap();
      this._initCursorStyle();
      this._initMapUi(defaultLayers);
      this._initBehavior();
      this._initClickListener();
    }
  }

  _initBehavior() {
    if (this._map) {
      this.behavior = new H.mapevents.Behavior(new H.mapevents.MapEvents(this._map));
      this.behavior.disable(window.H.mapevents.Behavior.Feature.WHEEL_ZOOM);
      this._initSmoothWheelZoom();
    }
  }

  _initSmoothWheelZoom() {
    const container = this._containerRef as HTMLElement;
    let pinchGeo: { lat: number; lng: number } | null = null;

    container.addEventListener(
      'wheel',
      (e: WheelEvent) => {
        e.preventDefault();

        const map = this._map!;

        // Snapshot state once at the start of a new gesture (map is at rest, screenToGeo is accurate)
        if (this._targetZoom === null) {
          this._targetZoom = map.getZoom();
          const c = map.getCenter();
          this._targetCenter = { lat: c.lat, lng: c.lng };
          const rect = container.getBoundingClientRect();
          const g = map.screenToGeo(e.clientX - rect.left, e.clientY - rect.top);
          pinchGeo = { lat: g.lat, lng: g.lng };
        }

        let delta = e.deltaY;
        if (e.deltaMode === 1) delta *= 20;
        if (e.deltaMode === 2) delta *= 300;

        const rawStep = -delta / 600;
        const zoomStep = Math.max(-0.3, Math.min(0.3, rawStep));
        const prevTargetZoom = this._targetZoom;
        // @ts-ignore
        this._targetZoom = Math.max(2, Math.min(20, this._targetZoom + zoomStep));

        // Shift target center so pinchGeo stays fixed under the cursor
        // @ts-ignore
        const scaleFactor = Math.pow(2, prevTargetZoom - this._targetZoom);
        this._targetCenter = {
          lat: pinchGeo!.lat + (this._targetCenter!.lat - pinchGeo!.lat) * scaleFactor,
          lng: pinchGeo!.lng + (this._targetCenter!.lng - pinchGeo!.lng) * scaleFactor,
        };

        map.getViewModel().setLookAtData(
          { zoom: this._targetZoom, position: this._targetCenter },
          600
        );

        if (this._wheelTimeout) {
          clearTimeout(this._wheelTimeout);
        }
        this._wheelTimeout = setTimeout(() => {
          this._targetZoom = null;
          this._targetCenter = null;
          pinchGeo = null;
        }, 1000);
      },
      { passive: false }
    );
  }

  _getBaseLayer() {
    const currentLayer = this._layers.get('baseLayer');
    if (currentLayer) {
      return currentLayer;
    }
    const rasterTile = this._platform.getRasterTileService({
      queryParams: {
        style: 'logistics.day',
        size: 256,
        lang: this._lang,
      },
    });
    const resterTileProvider = new window.H.service.rasterTile.Provider(rasterTile);
    const tileLayer = new window.H.map.layer.TileLayer(resterTileProvider);
    this._layers.set('baseLayer', tileLayer);
    return tileLayer;
  }

  _getVehicleRestrictionsLayer() {
    const currentLayer = this._layers.get('VehicleRestrictionsLayer');
    if (currentLayer) {
      return currentLayer;
    }
    const rasterTile = this._platform.getRasterTileService({
      resource: 'blank',
      queryParams: {
        style: 'logistics.day',
        features: 'vehicle_restrictions:permanent_only',
        size: 512,
        lang: this._lang,
        ppi: 100,
      },
    });
    const resterTileProvider = new window.H.service.rasterTile.Provider(rasterTile);

    const tileLayer = new window.H.map.layer.TileLayer(resterTileProvider);
    this._layers.set('VehicleRestrictionsLayer', tileLayer);
    return tileLayer;
  }

  _getSatelliteMap() {
    const currentLayer = this._layers.get('satelliteLayer');
    if (currentLayer) {
      return currentLayer;
    }
    const rasterTileService = this._platform.getRasterTileService({
      queryParams: {
        style: 'explore.satellite.day',
        features: this._mapFeatures,
        size: this._defineTileSize(),
        lang: this._lang,
        ppi: this._defineTilePPI(),
      },
    });
    const rasterTileProvider = new window.H.service.rasterTile.Provider(rasterTileService);
    const tileLayer = new window.H.map.layer.TileLayer(rasterTileProvider);
    tileLayer.tileStyle = 'explore.satellite.day';
    this._layers.set('satelliteLayer', tileLayer);
    return tileLayer;
  }

  _defineTilePPI() {
    switch (!0) {
      case window.screen.height > this._largeBreakpoints.height && window.screen.width > this._largeBreakpoints.width:
        return this._tilePPI.LARGE;
      case window.screen.height > this._middleBreakpoints.height &&
        window.screen.width > this._middleBreakpoints.height:
        return this._tilePPI.MIDDLE;
      default:
        return this._tilePPI.DEFAULT;
    }
  }

  _defineTileSize() {
    return this._pixelRatio === 1 ? 256 : 512;
  }

  _initMapUi(defaultLayers: any) {
    if (this._map) {
      this._mapUI = window.H.ui.UI.createDefault(this._map, defaultLayers);
      this._mapUI.removeControl('zoom');
      this._mapUI.removeControl('mapsettings');
    }
  }

  _resizeMap() {
    // Enable dynamic resizing of the map, based on the current size of the enclosing container
    window.addEventListener('resize', () => {
      if (this._map) {
        this._map.getViewPort().resize();
      }
    });
  }

  _initClickListener() {
    if (this._map) {
      this._map.addEventListener('tap', (event: any) => {
        this._revealCluster(event);
      });
    }
  }

  private _revealCluster({ target }: any) {
    // @ts-ignore
    const { type, clusterExpansionZoom } = target && target.getData ? target.getData() || {} : {};

    if (type === 'cluster' && clusterExpansionZoom) {
      const { lat, lng } = target.getGeometry() || {};

      this._map.getViewModel().setLookAtData(
        {
          position: { lat, lng },
          zoom: clusterExpansionZoom + 1
        },
        2
      );
    }
  }

  _initCursorStyle() {
    const mapDOMElement = document.getElementById('map');

    this._map.addEventListener('pointermove', (event: any) => {
      if (mapDOMElement) {
        const { type } = (event.target && event.target.getData && event.target.getData()) || {};

        if (type === 'cluster' || type === 'marker') {
          mapDOMElement.style.cursor = 'pointer';
        } else {
          mapDOMElement.style.cursor = '';
        }
      }
    });

    this._map.addEventListener('dragstart', () => {
      if (mapDOMElement) {
        mapDOMElement.style.cursor = 'grab';
      }
    });

    this._map.addEventListener('dragend', () => {
      if (mapDOMElement) {
        mapDOMElement.style.cursor = '';
      }
    });
  }

  get mapUi() {
    return this._mapUI;
  }

  addBubble(position: H.geo.IPoint, options: H.ui.InfoBubble.Options): void {
    const bubble = new H.ui.InfoBubble(position, options);
    this._mapUI.addBubble(bubble);
  }

  getNativeMap(): Here | undefined {
    return this._map;
  }

  getPlatform(): any {
    return this._platform;
  }

  fitBounds({ sw, ne }: FitBounds, padding: MapPadding = {}) {
    const { top = 0, right = 0, bottom = 0, left = 0 } = padding;
    this._map.getViewPort().setPadding(top, right, bottom, left);
    this._map.getViewModel().setLookAtData(
      {
        bounds: new window.H.geo.Rect(sw?.lat, sw?.lng, ne?.lat, ne?.lng),
      },
      1.25
    );
  }

  zoomIn() {
    if (this._map) {
      const currentZoom = this._map.getZoom() || 0;
      this._map.setZoom(currentZoom + 1);
    }
  }

  zoomOut() {
    if (this._map) {
      const currentZoom = this._map.getZoom() || 0;
      this._map.setZoom(currentZoom - 1);
    }
  }

  setZoom(value: Zoom) {
    this._map?.setZoom(value);
  }

  getZoom() {
    if (this._map) {
      return this._map.getZoom() || 0;
    }
  }

  setCenter({ lat, lng }: CenterCoords = {}) {
    this._map?.setCenter({
      lat,
      lng,
    });
  }

  setMapType(mapType: MapType) {
    let layer = null;

    if (mapType === 'roadmap') {
      layer = this._getBaseLayer();
    }

    if (mapType === 'satellite') {
      layer = this._getSatelliteMap();
    }

    if (layer === null) {
      return;
    }

    this._map.setBaseLayer(layer);
  }

  enableTrafficLayer() {
    this._map.addLayer(this._defaultLayers.vector.traffic.map, 1);
  }

  disableTrafficLayer() {
    this._map.removeLayer(this._defaultLayers.vector.traffic.map);
  }

  addListenerDragStart(fn: OnMapEventListener): MapEventListener {
    return undefined;
  }

  addListenerClick(fn: OnMapEventListener): MapEventListener {
    return this._map.addEventListener('tap', fn);
  }

  addListenerBoundsChanged(fn: OnChangeBounds): MapEventListener {
    return undefined;
  }

  addListenerStreetViewChange(fn: OnChangeMapStreetView): MapEventListener {
    return undefined;
  }

  addListenerMapViewChange(fn: OnChangeMapStreetView): MapEventListener {
    return this._map.addEventListener('mapviewchange', fn);
  }
  removeListenerMapViewChange(fn: OnChangeMapStreetView): MapEventListener {
    return this._map.removeEventListener('mapviewchange', fn);
  }
}
